import { supabase } from "@/integrations/supabase/client";
import { whatsappIntegrationDisplayLabel } from "@/lib/whatsapp-integration-label";
import type { MemberIntegrationAccessContext } from "@/lib/conversation-filters";

export type { MemberIntegrationAccessContext };

export const WHATSAPP_INTEGRATION_PROVIDERS = [
  "whatsapp_evolution",
  "whatsapp_cloud",
  "whatsapp_uazapi",
] as const;

export type MessagingIntegrationOption = {
  id: string;
  display_name: string;
  provider: string;
};

type IntegrationRow = {
  id: string;
  provider: string;
  config: Record<string, unknown> | null;
  instance_identifier: string | null;
};

export async function fetchMemberIntegrationAccessContext(
  workspaceId: string,
): Promise<MemberIntegrationAccessContext> {
  const { data, error } = await supabase.rpc("get_member_integration_access_context", {
    p_workspace_id: workspaceId,
  });
  if (error) throw error;
  const row = (data ?? {}) as Record<string, unknown>;
  return {
    isOwner: row.is_owner === true,
    workspaceRestricted: row.workspace_restricted === true,
    allowedIntegrationIds: new Set(
      Array.isArray(row.integration_ids) ? (row.integration_ids as string[]) : [],
    ),
  };
}

export async function fetchMemberAccessibleIntegrationIds(
  workspaceId: string,
  userId: string,
): Promise<Set<string>> {
  const { data, error } = await supabase.rpc("member_accessible_integration_ids", {
    _user_id: userId,
    _workspace_id: workspaceId,
  });

  if (error) throw error;
  return new Set((data as string[] | null) ?? []);
}

export async function fetchMemberAccessibleWhatsAppIntegrations(
  workspaceId: string,
  userId: string,
): Promise<MessagingIntegrationOption[]> {
  const [{ data: integrations, error }, accessibleIds] = await Promise.all([
    supabase
      .from("integrations")
      .select("id, provider, config, instance_identifier")
      .eq("workspace_id", workspaceId)
      .in("provider", [...WHATSAPP_INTEGRATION_PROVIDERS])
      .eq("status", "active")
      .order("created_at", { ascending: true }),
    fetchMemberAccessibleIntegrationIds(workspaceId, userId),
  ]);

  if (error) throw error;

  return ((integrations as IntegrationRow[] | null) ?? [])
    .filter((integration) => accessibleIds.has(integration.id))
    .map((integration) => ({
      id: integration.id,
      provider: integration.provider,
      display_name: whatsappIntegrationDisplayLabel(integration),
    }));
}
